import axios from 'axios'
import { ensureDeviceId } from '@/assets/js/device.js'
import { getInstallChannel, INSTALL_CHANNEL_HEADER } from '@/assets/js/installChannel'
import { getSecurityHeaders } from '@/assets/js/securitySignals'
import { getIdToken } from './amplifyAuth'

const API_BASE_URL = process.env.VUE_APP_API_URL?.trim().replace(/\/$/, '') || ''

const DEVICE_TYPE = 'web'

function getStoredIdToken () {
  try {
    const raw = localStorage.getItem('cognito_tokens')
    if (!raw) return null
    const tokens = JSON.parse(raw)
    if (!tokens?.idToken) return null
    if (Number(tokens.expiresAt || 0) <= Date.now()) return null
    return tokens.idToken
  } catch {
    return null
  }
}

async function applyCommonHeaders (config) {
  if (!config.headers) {
    config.headers = {}
  }
  config.headers['X-Device-Id'] = await ensureDeviceId()
  config.headers['X-Device-Type'] = DEVICE_TYPE
  Object.assign(config.headers, getSecurityHeaders())
  const installChannel = getInstallChannel()
  if (installChannel) {
    config.headers[INSTALL_CHANNEL_HEADER] = installChannel
  }
  return config
}

async function applyAuthHeader (config) {
  const idToken = (await getIdToken()) || getStoredIdToken()
  if (idToken) {
    config.headers.Authorization = idToken.startsWith('Bearer ')
      ? idToken
      : `Bearer ${idToken}`
  }
  return config
}

// 1. 创建 axios 实例
const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
    'X-Device-Type': DEVICE_TYPE
  }
})

// 2. 请求拦截器 (Request Interceptor)
service.interceptors.request.use(
  async (config) => {
    await applyCommonHeaders(config)
    await applyAuthHeader(config)
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 3. 响应拦截器 (Response Interceptor)
service.interceptors.response.use(
  response => {
    const res = response.data
    logger.debug('网络请求成功:', res)
    // 这里可以根据后端返回的状态码进行逻辑处理（如 200 成功，其他失败）
    if (res.code !== 0) {
      console.error('错误消息：', res.msg || 'Error')
      const err = new Error(res.msg || 'Error')
      err.code = res.code
      err.status = response.status || 200
      err.data = res.data
      return Promise.reject(err)
    }
    return res.data
  },
  error => {
    // Cloudflare 地区拦截：返回 403（响应体可能是 HTML），当普通接口失败处理，
    // 不解析响应体、不弹 toast，交由各页面走既有 error 态。
    if (error.response?.status === 403) {
      const blockedError = new Error('Forbidden')
      blockedError.status = 403
      blockedError.blocked = true
      return Promise.reject(blockedError)
    }

    if (error.response && error.response.data) {
      console.error('网络请求错误：', error.response.data)
      error.status = error.response.status || 0
      return Promise.reject(error)
    } else {
     // 统一处理 HTTP 错误状态码 (401, 404, 500 等)
     console.error('网络请求错误：', error)
     error.status = error.response?.status || 0
     return Promise.reject(error)
    }
  }
)

export default service
